import { useQuery } from '@tanstack/react-query'
import { useAuthenticatedClient } from './queries'
import type { components } from './schema'

// A product's store 3D model (USA-1624). The record says whether the store has it; the model file is
// rebuilt from the product's AutoQuotes 3D file on request, so unpublished products show one too.
export type ProductModel3DStatus = components['schemas']['ProductModel3DStatusRead']
export type ProductModel3D = components['schemas']['ProductModel3DRead']

export const productModelKeys = {
  status: (productUuid: string) => ['products', 'model-3d', productUuid] as const,
  glb: (productUuid: string) => ['products', 'model-3d-glb', productUuid] as const,
}

/** Why a 3D file could not become a model: the converter's reason code and its detail. */
export class ModelUnavailableError extends Error {
  readonly reason: string
  readonly detail: string

  constructor(reason: string, detail: string) {
    super(reason)
    this.reason = reason
    this.detail = detail
  }
}

export function useProductModel3D(productUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: productModelKeys.status(productUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/{uuid}/model-3d', {
        params: { path: { uuid: productUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!productUuid,
  })
}

/** The model file as a Blob. Converting takes a second or two, so a loaded model is kept an hour. */
export function useProductModel3DFile(productUuid: string, enabled: boolean) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: productModelKeys.glb(productUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error, response } = await client.GET(
        '/dashboard/products/{uuid}/model-3d/glb',
        {
          params: { path: { uuid: productUuid } },
          parseAs: 'blob',
        },
      )
      if (error !== undefined || !data) {
        const detail = (error as { detail?: unknown } | undefined)?.detail
        if (detail && typeof detail === 'object' && 'reason' in detail) {
          const { reason, detail: text } = detail as {
            reason?: unknown
            detail?: unknown
          }
          throw new ModelUnavailableError(String(reason), typeof text === 'string' ? text : '')
        }
        throw new Error(`The 3D model could not be loaded (${response.status})`)
      }
      return data as Blob
    },
    enabled: enabled && !!productUuid,
    staleTime: 60 * 60 * 1000,
    retry: (count, error) => !(error instanceof ModelUnavailableError) && count < 1,
  })
}
